import { DOCUMENT } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { Observable, finalize, tap } from 'rxjs';
import { ApiService } from '@core/services/api/api.service';
import { DirectionService } from '@core/services/direction.service';
import {
  KlDesignPreferenceDialogService,
  type KlDesignPreferenceResult,
} from '@core/services/kl-design-preference-dialog.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { isAppOrTokenRoleAdmin } from '@core/utils/keycloak-app-admin';
import {
  readSavedColumnProperties,
  toSavedColumnFormat,
} from '@shared/components/kl-report/kl-report-column-properties.util';
import { GridColumn, GridDesignColumn } from '../grid.models';

export interface GridDesignTemplate {
  id: string | number;
  description: string;
  content: string;
  level: string;
  level_values?: string[];
  level_default: string;
  columns?: GridDesignColumn[];
}

export interface ColumnVisibilityRow {
  header: string;
  binding: string;
  visible: boolean;
  width?: number;
}

type DrawerTabId = 'columns' | 'design';

interface DrawerTabDef {
  id: DrawerTabId;
  labelKey: string;
}

const COLUMN_SETTINGS_DRAWER_BODY_CLASS = 'kl-column-settings-drawer-open';

@Component({
  selector: 'kl-grid-column-settings-drawer',
  standalone: false,
  templateUrl: './kl-grid-column-settings-drawer.html',
  styleUrl: './kl-grid-column-settings-drawer.scss',
})
export class KlGridColumnSettingsDrawer implements OnDestroy {
  readonly visible = input(false);
  readonly visibleChange = output<boolean>();

  readonly columnDefinitions = input.required<GridColumn[]>();
  readonly endpointName = input('');
  /** Parent module of form-grid designs (e.g. `APPLICATION_PARAMS`). */
  readonly designParentModule = input('');
  /** Design module (e.g. `CASHIER_AUTH_GROUP_PERMISSION_FORM`). */
  readonly designModule = input('');
  /** Design key / grid name (e.g. `grdCashierAuthGroupPermissionLine`). */
  readonly designKey = input('');
  /** When false, only the column picker tab is shown (no saved grid designs). */
  readonly showGridDesignTab = input(true);
  /** i18n key for the drawer title. */
  readonly drawerHeaderKey = input('template_list');
  /** Which tab to show when the drawer opens. */
  readonly initialTab = input<'columns' | 'design'>('columns');
  /** PrimeNG overlay target (use grid host in native fullscreen). */
  readonly appendTo = input<unknown>('body');
  /** Snapshots live header widths from the grid before building a design payload. */
  readonly captureColumnWidths = input<(() => void) | undefined>();
  /** Opt-in (custom reports): a gear per column opens its field properties; saved designs keep each column's format/aggregate. */
  readonly showColumnProperties = input(false);
  /** Mirrors {@link KlGridContainer.columnWidthEditMode}: save-layout prompt after resize when on. */
  readonly columnWidthEditMode = input(false);
  readonly columnWidthEditModeChange = output<boolean>();
  /** Form grids: saved design content stores `visible` inverted (`true` = hidden). */
  readonly invertDesignVisibility = input(false);

  readonly resetToDefault = output<void>();
  readonly columnsChanged = output<GridColumn[]>();
  /** Binding of the column whose field properties gear was clicked (with {@link showColumnProperties}). */
  readonly columnPropertiesClick = output<string>();

  readonly drawerStyle = { width: '340px', zIndex: 11001 };
  readonly drawerMaskStyle = { zIndex: 11000 };
  readonly drawerPosition = computed(() => (this.directionService.isRtl() ? 'left' : 'right'));
  readonly drawerTabs = computed<DrawerTabDef[]>(() => {
    const tabs: DrawerTabDef[] = [
      { id: 'design', labelKey: 'grid_design' },
      { id: 'columns', labelKey: 'column_add_or_remove' },
    ];
    return this.directionService.isRtl() ? [...tabs].reverse() : tabs;
  });

  readonly activeDrawerTab = signal<'columns' | 'design'>('columns');

  readonly columnVisibility = signal<ColumnVisibilityRow[]>([]);
  readonly columnSearchText = signal('');
  /** Binding of the column whose display name is currently being edited (reference: pencil-icon rename). */
  readonly editingColumnBinding = signal<string | null>(null);
  readonly editingColumnLabel = signal('');

  readonly designTemplates = signal<GridDesignTemplate[]>([]);
  readonly currentDesignTemplate = signal<GridDesignTemplate | null>(null);
  readonly designSaving = signal(false);
  /** While a child modal (design preference dialog) is open above this drawer. */
  readonly childOverlayOpen = signal(false);
  readonly legacyDesignQueryActive = computed(() => !!this.designModule().trim() && !!this.designKey().trim());

  readonly hasDesignSaveTarget = computed(() => {
    if (this.endpointName().trim()) {
      return true;
    }
    return !!this.designModule().trim() && !!this.designKey().trim();
  });

  /** True once a saved design has been applied to the live grid (init or explicit pick). */
  private gridDesignApplied = false;
  private initialDesignLoadStarted = false;
  private drawerWasOpen = false;
  /** User changed column layout (resize/reorder) — blocks late init design apply. */
  private liveLayoutTouched = false;
  /** Monotonic token so stale async design/filter responses are ignored. */
  private designLoadGeneration = 0;

  private readonly directionService = inject(DirectionService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly api = inject(ApiService);
  private readonly userEnv = inject(UserEnvService);
  // Optional: unit tests that render kl-grid run without a Keycloak provider.
  private readonly keycloak = inject(Keycloak, { optional: true });
  private readonly preferenceDialog = inject(KlDesignPreferenceDialogService);
  private readonly document = inject(DOCUMENT);

  constructor() {
    // Open transition: run once when visible goes false → true.
    // Sets the active tab from initialTab, refreshes the saved-design list, and
    // seeds columnVisibility. Must NOT reset activeDrawerTab on later effect runs
    // (e.g. when columnDefinitions change after deleting the active design).
    effect(() => {
      const open = this.visible();
      const tab = this.initialTab();
      if (open && !this.drawerWasOpen) {
        this.activeDrawerTab.set(tab);
        if (this.shouldRefreshDesignTemplatesOnOpen()) {
          this.loadDesignTemplates();
        }
        this.buildColumnVisibility();
        this.cdr.markForCheck();
      }
      this.drawerWasOpen = open;
    });

    // While open, keep columnVisibility in sync with live grid columns (resize,
    // parent reset, etc.) without touching activeDrawerTab or re-applying designs.
    effect(() => {
      if (!this.visible()) {
        return;
      }
      this.columnDefinitions();
      this.buildColumnVisibility();
      this.cdr.markForCheck();
    });

    effect(() => {
      const open = this.visible();
      untracked(() => this.setBodyDrawerOpenClass(open));
    });
  }

  onDrawerVisibleChange(open: boolean): void {
    this.visibleChange.emit(open);
  }

  ngOnDestroy(): void {
    this.setBodyDrawerOpenClass(false);
  }

  private setBodyDrawerOpenClass(open: boolean): void {
    this.document.body.classList.toggle(COLUMN_SETTINGS_DRAWER_BODY_CLASS, open);
  }

  setDrawerTab(tab: 'columns' | 'design'): void {
    this.activeDrawerTab.set(tab);
    this.cdr.markForCheck();
  }

  toggleColumnWidthEditMode(): void {
    this.columnWidthEditModeChange.emit(!this.columnWidthEditMode());
    this.onDrawerVisibleChange(false);
  }

  syncAfterParentReset(): void {
    this.buildColumnVisibility();
    this.cdr.markForCheck();
  }

  /** Called when the user resizes or reorders columns on the live grid. */
  markLiveLayoutTouched(): void {
    this.liveLayoutTouched = true;
  }

  readonly filteredColumnVisibility = computed<ColumnVisibilityRow[]>(() => {
    if (!this.columnSearchText().trim()) return this.columnVisibility();
    const term = this.columnSearchText().toLowerCase();
    return this.columnVisibility().filter(
      (col) => col.header.toLowerCase().includes(term) || col.binding.toLowerCase().includes(term)
    );
  });

  private buildColumnVisibility(): void {
    this.columnVisibility.set(this.toColumnVisibilityRows(this.columnDefinitions()));
  }

  private toColumnVisibilityRows(cols: GridColumn[]): ColumnVisibilityRow[] {
    return cols.map((col) => ({
      header: col.header ? this.translate.instant(col.header) : (col.binding ?? ''),
      binding: col.binding ?? '',
      visible: col.visible !== false,
      width: this.resolveColumnWidth(col.width),
    }));
  }

  private resolveColumnWidth(width?: number | string): number | undefined {
    if (typeof width === 'number' && Number.isFinite(width)) {
      return width;
    }
    if (typeof width === 'string' && width !== '*' && width.trim() !== '') {
      const parsed = Number.parseInt(width.replace(/px$/i, '').trim(), 10);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  onColumnDrop(event: CdkDragDrop<ColumnVisibilityRow[]>): void {
    if (this.columnSearchText().trim()) {
      return;
    }
    const filtered = this.filteredColumnVisibility();
    const dragged = filtered[event.previousIndex];
    const target = filtered[event.currentIndex];
    if (!dragged || !target) {
      return;
    }
    const full = [...this.columnVisibility()];
    const fromIdx = full.findIndex((c) => c.binding === dragged.binding);
    const toIdx = full.findIndex((c) => c.binding === target.binding);
    if (fromIdx < 0 || toIdx < 0) {
      return;
    }
    moveItemInArray(full, fromIdx, toIdx);
    this.columnVisibility.set(full);
    this.emitColumnsChanged();
    this.cdr.markForCheck();
  }

  startEditColumnLabel(row: ColumnVisibilityRow): void {
    this.editingColumnBinding.set(row.binding);
    this.editingColumnLabel.set(row.header);
  }

  saveColumnLabel(binding: string): void {
    const label = this.editingColumnLabel().trim();
    this.editingColumnBinding.set(null);
    if (!label) {
      return;
    }

    this.columnVisibility.set(
      this.columnVisibility().map((c) => (c.binding === binding ? { ...c, header: label } : c))
    );

    const renamed = this.columnDefinitions().map((c) => (c.binding === binding ? { ...c, header: label } : c));
    this.columnsChanged.emit(renamed);
    this.cdr.markForCheck();
  }

  toggleColumnVisibility(binding: string): void {
    const updated = this.columnVisibility().map((c) => (c.binding === binding ? { ...c, visible: !c.visible } : c));
    this.columnVisibility.set(updated);
    this.emitColumnsChanged();
    this.cdr.markForCheck();
  }

  checkAllColumns(): void {
    this.columnVisibility.set(this.columnVisibility().map((col) => ({ ...col, visible: true })));
    this.emitColumnsChanged();
    this.cdr.markForCheck();
  }

  uncheckAllColumns(): void {
    this.columnVisibility.set(this.columnVisibility().map((col) => ({ ...col, visible: false })));
    this.emitColumnsChanged();
    this.cdr.markForCheck();
  }

  onColumnWidthChange(binding: string, value: number | string | null): void {
    const width = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(width)) {
      return;
    }
    this.changeColumnWidth(binding, width);
  }

  changeColumnWidth(binding: string, width: number): void {
    const defs = this.columnDefinitions();
    const defMap = new Map(defs.map((c) => [c.binding ?? '', c]));

    const updated = this.columnVisibility().map((row) => {
      if (row.binding === binding) {
        return { ...row, width };
      }
      const existing = row.width ?? this.resolveColumnWidth(defMap.get(row.binding)?.width);
      return existing != null ? { ...row, width: existing } : row;
    });

    this.columnVisibility.set(updated);
    this.emitColumnsChanged();
    this.cdr.markForCheck();
  }

  onResetToDefaultClick(): void {
    this.currentDesignTemplate.set(null);
    this.gridDesignApplied = false;
    this.liveLayoutTouched = false;
    this.resetToDefault.emit();
  }

  /**
   * Load saved grid designs once when the grid mounts, so the DEFAULT design shows on first render.
   *
   * Called from {@link KlGridContainer} in `ngAfterViewInit` when the grid has a
   * design save target. Uses `applyToGrid: true` so the DEFAULT template is applied
   * to the live grid via {@link selectDesignTemplate} exactly once (`gridDesignApplied`
   * guard). Subsequent drawer opens call {@link loadDesignTemplates} with
   * `applyToGrid: false` so in-memory resize/layout changes are not overwritten.
   *
   * Supports module-based `design/filter` (module + key) and endpoint-based
   * `design/parent/grid/{endpoint}/default` APIs.
   */
  loadInitialGridDesign(): void {
    if (this.gridDesignApplied || this.initialDesignLoadStarted) {
      return;
    }
    this.initialDesignLoadStarted = true;

    if (this.legacyDesignQueryActive()) {
      this.loadLegacyDesignTemplates(true);
      return;
    }

    const ep = this.endpointName();
    if (!ep) {
      return;
    }

    const generation = this.beginDesignLoad();
    this.api.getAllData<any[]>(`design/parent/grid/${ep}/default`).subscribe({
      next: (templateRows) => {
        this.applyLoadedDesignRows(Array.isArray(templateRows) ? templateRows : [], true, generation);
      },
      error: () => {},
    });
  }

  private shouldRefreshDesignTemplatesOnOpen(): boolean {
    if (this.designTemplates().length > 0) {
      return false;
    }
    if (this.initialDesignLoadStarted) {
      return false;
    }
    return true;
  }

  private beginDesignLoad(): number {
    this.designLoadGeneration += 1;
    return this.designLoadGeneration;
  }

  private loadDesignTemplates(): void {
    if (this.legacyDesignQueryActive()) {
      this.loadLegacyDesignTemplates(false);
      return;
    }

    const ep = this.endpointName();
    if (!ep) return;

    const generation = this.beginDesignLoad();
    this.api.getAllData<any[]>(`design/parent/grid/${ep}/default`).subscribe({
      next: (templateRows) => {
        this.applyLoadedDesignRows(Array.isArray(templateRows) ? templateRows : [], false, generation);
      },
      error: () => {},
    });
  }

  private resolveCompanyId(): number | null {
    const id = this.userEnv.env()?.company?.id;
    if (id == null) {
      return null;
    }
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }

  private loadLegacyDesignTemplates(applyToGrid: boolean): void {
    const companyId = this.resolveCompanyId();
    if (!companyId) {
      return;
    }

    const parent = this.designParentModule().trim() || 'APPLICATION_PARAMS';
    const module = this.designModule().trim();
    const key = this.designKey().trim();

    const generation = this.beginDesignLoad();
    this.api
      .getFilteredData<Record<string, unknown>>('design', {
        parent: { module: parent },
        product_type: 'JRETAIL',
        module,
        key,
        type: 'GRID_DESIGN',
        company_id: companyId,
      })
      .subscribe({
        next: (res) => {
          const rows = (res?.data as Record<string, unknown>[] | undefined) ?? [];
          // The response can also hold other grids' designs and the module's fast-filter record.
          const designRows = rows.filter(
            (row) => row['module'] === module && row['key'] === key && (!row['type'] || row['type'] === 'GRID_DESIGN')
          );
          this.applyLoadedDesignRows(designRows, applyToGrid, generation);
        },
        error: () => {},
      });
  }

  /**
   * Parse `design/filter` (or endpoint default) rows into {@link designTemplates}.
   *
   * @param applyToGrid When true (init load only), apply the last DEFAULT template
   *   to the live grid via {@link selectDesignTemplate}. When false (drawer refresh),
   *   only update the template list and highlight DEFAULT in the UI—never emit
   *   {@link columnsChanged}, so resized column widths are preserved.
   */
  private applyLoadedDesignRows(rows: Record<string, unknown>[], applyToGrid: boolean, generation: number): void {
    if (generation !== this.designLoadGeneration) {
      return;
    }

    const templates = rows.map((t) => ({
      id: KlGridColumnSettingsDrawer.readDesignId(t['id']),
      description: String(t['description'] ?? ''),
      content: String(t['content'] ?? ''),
      level: String(t['level'] ?? ''),
      level_values: (t['level_values'] as string[] | undefined) ?? [],
      level_default: String(t['level_default'] ?? ''),
      columns: this.parseDesignContent(String(t['content'] ?? '')),
    }));
    this.designTemplates.set(templates);

    // Last DEFAULT in the list wins; ids are often Mongo strings, so list order decides, not id order.
    const defaults = templates.filter((t) => t.level_default === 'DEFAULT');
    const def = defaults.length > 0 ? defaults[defaults.length - 1] : undefined;
    if (def) {
      if (applyToGrid && !this.gridDesignApplied && !this.liveLayoutTouched) {
        this.selectDesignTemplate(def);
      } else if (!this.currentDesignTemplate()) {
        this.currentDesignTemplate.set(def);
      }
    }
    this.cdr.markForCheck();
  }

  private static readDesignId(raw: unknown): string | number {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim() !== '') return raw;
    return String(raw ?? '');
  }

  private parseDesignContent(content: string): GridDesignColumn[] {
    try {
      const parsed = JSON.parse(content);
      const columns: GridDesignColumn[] = parsed?.columns ?? [];
      if (!this.invertDesignVisibility()) {
        return columns;
      }
      return columns.map((column) => ({ ...column, visible: column.visible !== true }));
    } catch {
      return [];
    }
  }

  selectDesignTemplate(template: GridDesignTemplate): void {
    this.currentDesignTemplate.set(template);
    if (!template.columns?.length) return;

    const cols = this.columnDefinitions();
    const ordered: GridColumn[] = [];
    for (const tc of template.columns) {
      const match = cols.find((c) => c.binding === tc.binding);
      if (match) {
        ordered.push(
          this.withDesignColumnProperties(
            {
              ...match,
              visible: tc.visible,
              width: tc.width ?? match.width,
              header: tc.header ?? match.header,
            },
            tc
          )
        );
      }
    }
    // A saved template predates dynamically-added columns (e.g. a picker's selection checkbox);
    // append those, except a checkbox column, which always belongs first regardless of the template.
    for (const c of cols) {
      if (ordered.find((o) => o.binding === c.binding)) continue;
      const column = this.withDesignColumnProperties(c);
      if (c.multi_select) {
        ordered.unshift(column);
      } else {
        ordered.push(column);
      }
    }

    this.columnVisibility.set(this.toColumnVisibilityRows(ordered));

    this.gridDesignApplied = true;
    this.columnsChanged.emit(ordered);
    this.cdr.markForCheck();
  }

  /** With column properties on, a column's field properties come only from the applied design (none when it lacks the column). */
  private withDesignColumnProperties(column: GridColumn, saved?: GridDesignColumn): GridColumn {
    if (!this.showColumnProperties()) {
      return column;
    }
    return { ...column, columnProperties: saved ? readSavedColumnProperties(saved) : undefined };
  }

  /**
   * Opens the grid design save flow without showing this drawer (e.g. after the user
   * confirms the post-resize save prompt on {@link KlGridContainer}).
   */
  promptSaveDesign(): void {
    this.saveDesign();
  }

  saveDesign(): void {
    if (this.designSaving()) return;

    if (this.legacyDesignQueryActive()) {
      this.saveLegacyDesign('add');
      return;
    }

    const ep = this.endpointName();
    if (!ep) return;

    this.designSaving.set(true);
    const columnsData = this.prepareDesignColumnsPayload();
    const name = this.translate.instant('grid_design') + ' ' + (this.designTemplates().length + 1);

    const payload = {
      parent: { module: 'grid' },
      level: 'USER',
      level_values: [],
      level_default: 'NO_DEFAULT',
      description: name,
      module: ep,
      key: 'default',
      type: 'GRID_DESIGN',
      content: JSON.stringify({ columns: columnsData }),
    };

    this.api.addData<any>('design', payload).subscribe({
      next: (savedTemplate) => {
        const newTemplate: GridDesignTemplate = {
          id: savedTemplate.id,
          description: savedTemplate.description,
          content: savedTemplate.content,
          level: savedTemplate.level,
          level_values: savedTemplate.level_values,
          level_default: savedTemplate.level_default,
          columns: this.parseDesignContent(savedTemplate.content),
        };
        this.designTemplates.set([...this.designTemplates(), newTemplate]);
        this.currentDesignTemplate.set(newTemplate);
        this.designSaving.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.designSaving.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  private saveLegacyDesign(mode: 'add' | 'edit', template?: GridDesignTemplate): void {
    const companyId = this.resolveCompanyId();
    const parent = this.designParentModule().trim() || 'APPLICATION_PARAMS';
    const module = this.designModule().trim();
    const key = this.designKey().trim();
    if (!companyId || !module || !key) {
      return;
    }

    let currentData: Record<string, unknown> | null = null;
    if (mode === 'edit' && template) {
      currentData = {
        id: template.id,
        description: template.description,
        level: template.level,
        level_values: template.level_values,
        level_default: template.level_default,
      };
    }

    this.childOverlayOpen.set(true);
    this.preferenceDialog
      .open({
        modalId: `grid-design-${parent}-${module}-${key}`,
        commitType: mode,
        nameLabelKey: 'design_name',
        currentData,
        header: mode === 'add' ? 'save_design' : 'edit_design_filter',
        submitAction: (result) => this.persistLegacyDesign(result, mode, template, parent, module, key, companyId),
      })
      .pipe(
        finalize(() => {
          this.childOverlayOpen.set(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  /**
   * POST/PUT a module-based grid design after the user confirms in {@link KlDesignPreferenceDialogService}.
   *
   * Builds the payload from {@link prepareDesignColumnsPayload} (which snapshots live
   * header widths via {@link captureColumnWidths} first). Returned as an `Observable`
   * so the preference dialog can keep its Save button in a loading state until the API
   * completes. On success, updates {@link designTemplates} and {@link currentDesignTemplate}.
   */
  private persistLegacyDesign(
    result: KlDesignPreferenceResult,
    mode: 'add' | 'edit',
    template: GridDesignTemplate | undefined,
    parent: string,
    module: string,
    key: string,
    companyId: number
  ): Observable<Record<string, unknown>> {
    const payload: Record<string, unknown> = {
      parent: { module: parent },
      level: result.level,
      level_values: result.levelValues,
      level_default: result.levelDesign ? 'DEFAULT' : 'NO_DEFAULT',
      description: result.entryName,
      module,
      key,
      type: 'GRID_DESIGN',
      product_type: 'JRETAIL',
      content: JSON.stringify({ columns: this.prepareDesignColumnsPayload(), frozen: 0 }),
      company_id: companyId,
    };

    if (mode === 'edit' && template) {
      payload['id'] = template.id;
    }

    const req =
      mode === 'edit'
        ? this.api.updateData<Record<string, unknown>>('design', payload)
        : this.api.addData<Record<string, unknown>>('design', payload);

    return req.pipe(
      tap((savedTemplate) => {
        const newTemplate: GridDesignTemplate = {
          id: KlGridColumnSettingsDrawer.readDesignId(savedTemplate['id'] ?? template?.id),
          description: String(savedTemplate['description'] ?? result.entryName),
          content: String(savedTemplate['content'] ?? payload['content']),
          level: String(savedTemplate['level'] ?? result.level),
          level_values: (savedTemplate['level_values'] as string[] | undefined) ?? result.levelValues,
          level_default: String(savedTemplate['level_default'] ?? payload['level_default']),
          columns: this.parseDesignContent(String(savedTemplate['content'] ?? payload['content'])),
        };

        if (mode === 'edit' && template) {
          this.designTemplates.set(this.designTemplates().map((t) => (t.id === template.id ? newTemplate : t)));
        } else {
          this.designTemplates.set([...this.designTemplates(), newTemplate]);
        }
        this.currentDesignTemplate.set(newTemplate);
        this.cdr.markForCheck();
      })
    );
  }

  /** Endpoint designs always belong to the current user; module designs need admin or USER-level ownership. */
  canManageDesign(template: GridDesignTemplate): boolean {
    if (!this.legacyDesignQueryActive()) {
      return true;
    }
    const env = this.userEnv.env();
    if (isAppOrTokenRoleAdmin(this.keycloak?.tokenParsed, env)) {
      return true;
    }
    const userId = env?.user_id;
    return template.level === 'USER' && userId != null && (template.level_values ?? []).includes(String(userId));
  }

  editDesign(template: GridDesignTemplate, event: MouseEvent): void {
    event.stopPropagation();
    this.saveLegacyDesign('edit', template);
  }

  deleteDesign(template: GridDesignTemplate, index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.api.deleteData('design', template.id).subscribe({
      next: () => {
        const updated = this.designTemplates().filter((_, i) => i !== index);
        this.designTemplates.set(updated);
        if (this.currentDesignTemplate()?.id === template.id) {
          this.currentDesignTemplate.set(null);
          this.resetToDefault.emit();
        }
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  private prepareDesignColumnsPayload(): GridDesignColumn[] {
    this.captureColumnWidths()?.();
    this.buildColumnVisibility();
    return this.buildDesignColumnsPayload();
  }

  private buildDesignColumnsPayload(): GridDesignColumn[] {
    const defs = this.columnDefinitions();
    const defMap = new Map(defs.map((c) => [c.binding ?? '', c]));

    return this.columnVisibility().map((col) => {
      const base = defMap.get(col.binding);
      const payload: GridDesignColumn = {
        binding: col.binding,
        visible: this.invertDesignVisibility() ? !col.visible : col.visible,
        // The grid's own header is the i18n key (or the user's renamed text), not the drawer's translated label.
        header: base?.header ?? col.header,
      };
      const width = this.resolveColumnWidth(base?.width) ?? col.width;
      if (typeof width === 'number' && Number.isFinite(width)) {
        payload.width = width;
      }
      return this.showColumnProperties() ? { ...payload, ...toSavedColumnFormat(base?.columnProperties) } : payload;
    });
  }

  private emitColumnsChanged(): void {
    const defs = this.columnDefinitions();
    const defMap = new Map(defs.map((c) => [c.binding ?? '', c]));
    const updated: GridColumn[] = [];
    for (const row of this.columnVisibility()) {
      const base = defMap.get(row.binding);
      if (base) {
        updated.push({
          ...base,
          visible: row.visible,
          width: row.width ?? base.width,
        });
      }
    }
    for (const c of defs) {
      if (!updated.some((u) => u.binding === c.binding)) {
        updated.push(c);
      }
    }
    this.columnsChanged.emit(updated);
  }
}
